import { useMemo } from 'react';
import { OutletContext } from 'applications/common/routing/outlet-context.js';
import { useSelector } from 'react-redux';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import MojitoCore from 'mojito/core';

const { useLocalizedParams } = MojitoPresentation.Hooks;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { uiStyle } = MojitoCore.Presentation;
const { useMenuContent, useMenuLoading } = MojitoModules.Common.Hooks;
const SportsMenu = MojitoModules.SportsMenu.module;
const { BannerPromotions, PromotionUtils } = MojitoModules.Promotions;
const SportTabSelector = MojitoModules.SportTabSelector.module;
const {
    types: { SPORT_TABS },
} = MojitoModules.SportTabSelector;
const { selectTimeZoneGmt } = MojitoServices.UserSettings.selectors;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const {
    FlexPane,
    DocumentHead,
    GlobalLinks,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    PageHeader,
    EmptyContent,
} = MojitoPresentation.Components;

const LeftPromotions = ({ config }) => {
    const { leftPromotions: promotionsConfig } = config;
    if (!PromotionUtils.hasLocationId(promotionsConfig)) {
        return;
    }

    return <BannerPromotions config={promotionsConfig} />;
};

const SportPageDocumentHead = ({ sportId, appContext, config, l10n }) => {
    const sportName = sportId && appContext.sportName(sportId);
    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;

    if (!sportName) {
        return null;
    }

    const headData = {
        title: '$SPORT_PAGE.PAGE_TITLE',
        keywords: '$SPORT_PAGE.PAGE_KEYWORDS',
        description: '$SPORT_PAGE.PAGE_DESCRIPTION',
        canonical: '$SPORT_PAGE.PAGE_CANONICAL',
        localizedParams: new Map([['sportName', sportName]]),
        params: {
            sportId: sportId,
        },
    };

    return <DocumentHead data={headData} metaFactory={new metaFactory(l10n)} />;
};

const toMenuNavigation = {
    competitions: NavigationPathBuilder.toSportCompetitions,
    matches: NavigationPathBuilder.toSportMatches,
    priceBoosts: NavigationPathBuilder.toSportPriceBoosts,
    preBuiltBetBuilders: NavigationPathBuilder.toSportPreBetBuilders,
    outrights: NavigationPathBuilder.toSportOutright,
    future: NavigationPathBuilder.toSportFuture,
    highlights: NavigationPathBuilder.toSportHighlights,
    meetings: NavigationPathBuilder.toSportMeetings,
};

export default function SportPage({ mojitoTools, children }) {
    const { config, appContext, l10n } = mojitoTools;
    const { primaryContentContainer, sportTabSelector, secondaryContentContainer, showBackButton } =
        config;
    const { sportId, typeId } = useLocalizedParams();

    const timeZone = useSelector(state => selectTimeZoneGmt(state));
    const contentInfo = useMenuContent(sportId, timeZone);
    const menuItemsLoading = useMenuLoading(timeZone);

    const headerConfig = useMemo(() => {
        const { sportBackgroundImages, pageHeader } = config;
        const bgOverride = { backgroundImage: { src: sportBackgroundImages[sportId] } };
        return uiStyle.merge(pageHeader, bgOverride);
    }, [sportId, config]);

    const outletContextValue = useMemo(
        () => ({ ...contentInfo, menuItemsLoading }),
        [contentInfo, menuItemsLoading]
    );

    const availableMenuNavigations = Object.keys(contentInfo).map(menuId =>
        toMenuNavigation[menuId](sportId)
    );
    // We will detect if URL navigation that user performs is strictly eligible to any available menu.
    // e.g. if user has soccer/competitions in URL then we will check if competitions are available among contentInfo keys.
    const isLanding = appContext.isContentShown(NavigationPathBuilder.toSport(sportId), true);
    const hasContent =
        isLanding ||
        availableMenuNavigations.some(navigation => appContext.isContentShown(navigation));

    const onBackClicked = () => {
        IntentActionTrigger.showHomePage(false);
    };

    return (
        <>
            <SportPageDocumentHead
                sportId={sportId}
                appContext={appContext}
                config={config}
                l10n={l10n}
            />
            <PageSplitLayout>
                {/* Primary content */}
                <FlexPane config={primaryContentContainer}>
                    <>
                        {!typeId && (
                            <PageHeader
                                config={headerConfig}
                                label={appContext.sportName(sportId)}
                                showBackButton={showBackButton}
                                onBackClicked={onBackClicked}
                            >
                                <SportTabSelector
                                    tabs={SPORT_TABS}
                                    shouldExcludeTab={name => !contentInfo.hasOwnProperty(name)}
                                    config={sportTabSelector}
                                    routeParams={{ sportId }}
                                />
                            </PageHeader>
                        )}
                        {hasContent && (
                            <OutletContext.Provider value={outletContextValue}>
                                {children}
                            </OutletContext.Provider>
                        )}
                    </>
                    {!menuItemsLoading && !hasContent && (
                        <EmptyContent config={config.emptyContent} />
                    )}
                </FlexPane>
                {/* Secondary content */}
                <FlexPane config={secondaryContentContainer}>
                    <SportsMenu selectedSportId={sportId} />
                    <GlobalLinks />
                    <LeftPromotions config={config} />
                </FlexPane>
            </PageSplitLayout>
        </>
    );
}
